/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
// eslint-disable-next-line no-unused-vars
import {DiscoverySource} from './Discovery';

export enum SessionScope {
  /**
   * User - Session is valid for the current user only.
   */
  USER = 'user',
  /**
   * Organization - Session is valid for the current user and organization only.
   */
  ORG = 'org',
  /**
   * Context - Session is valid for the current user, organization and context only.
   * Context depends on the solution - Could be Sandboxes or Sub orgs.
   */
  CONTEXT = 'context'
}

export enum HISTORY {
  HASH = 'HASH',
  HISTORY = 'HISTORY',
  SERVER = 'SERVER'
}

export enum RUNTIME {
  LEGACY,
  CDN
}

export enum RELEASE_TYPE {
  ALPHA = 'alpha',
  BETA = 'beta'
}

export enum HASH_TYPE {
  NOSLASH = 'noslash'
}

export interface HistoryConfig {
  type: HISTORY;
  config: {
    absolutePaths?: boolean;
    spinnerOnChange?: boolean;
    hashType?: HASH_TYPE;
  };
}

interface CodeExt {
  code: string;
  fi: string;
}

export interface Access {
  code?: string[];
  codeExt?: CodeExt;
  codeFlagLogicOp?: string;
  codeLogicOp?: string;
  flag?: string;
  sandboxFlag?: string;
}

export interface SessionConfig {
  scope: SessionScope;
  defaultTTL?: number;
}

interface EnvironmentMap<T> {
  [key: string]: T;
}

interface Gainsight {
  productKey: string;
}

interface IMSConfig {
  client_id: string;
  scope: string;
}

interface CustomIMSConfig extends IMSConfig {
  dev?: IMSConfig;
  qa?: IMSConfig;
  stage?: IMSConfig;
  prod?: IMSConfig;
}

interface NavItem {
  icon?: string;
  id?: string;
  name?: string;
  url?: string;
  heading?: string;
  items?: NavItem[];
}

interface SideNav {
  closeOnLoad?: boolean;
  dev?: string;
  qa?: string;
  stage?: string;
  prod?: string;
  menu?: NavItem[];
  workHubId?: string;
}

export interface Sandbox {
  hideInitialSpinner?: boolean;
  history: HISTORY | HistoryConfig;
  pageTimeout?: number;
  pathPrefix?: string;
  sources: EnvironmentMap<string | DiscoverySource>;
  ims?: CustomIMSConfig | IMSConfig;
  thirdParty?: boolean;
}

export interface UnifiedSearch {
  enabled: boolean;
}

export interface UrlContext {
  key: string;
  config?: {
    id?: string;
    name?: string;
    preferred?: string;
    serviceCode?: string;
  };
}

export interface Section {
  name: string;
  route: string;
  metricsAppId: string;
}

export interface Solution {
  analytics?: {
    code: string;
    scripts?: {
      [key: string]: string;
    };
  };
  appId: string;
  serviceCode?: string;
  settingsAppId?: string;
  base?: string;
  dashboard?: boolean;
  disableIfAppEnabled?: string[];
  distributionList: string[];
  experienceLeagueKey?: string;
  gainsight?: Gainsight;
  gqlEndpoint?: string;
  metricsAppId?: string;
  name?: string;
  path: string;
  permissionsPolicy?: string[];
  redirectFrom?: string[];
  refreshOnImsChange?: boolean;
  releaseType?: RELEASE_TYPE;
  restrictBySandbox?: boolean;
  runtime?: RUNTIME;
  sections?: Section[];
  spaAppId?: string;
  sandbox: Sandbox;
  session?: SessionConfig;
  sideNav?: SideNav;
  hero?: {
    icon: string;
    path: string;
    shortTitle: string;
    title: string;
  };
  hideTenant?: boolean;
  access?: Access;
  unifiedSearch?: UnifiedSearch;
  urlContext?: UrlContext;
  validClientIds?: string[];
}
